import dayjs, { type Dayjs } from 'dayjs';
import type { TaskItem, TaskPriority } from '@crm/shared';

export type TaskSort = 'manual' | 'due' | 'priority' | 'title' | 'created' | 'assignee';
export type DueFilter = 'any' | 'overdue' | 'today' | 'week' | 'none';

export interface TaskFilters {
  q: string;
  /** user ids, plus "unassigned" */
  assignees: string[];
  priorities: (TaskPriority | 'none')[];
  due: DueFilter;
}

export const EMPTY_FILTERS: TaskFilters = { q: '', assignees: [], priorities: [], due: 'any' };

export const activeFilterCount = (f: TaskFilters) => f.assignees.length + f.priorities.length + (f.due !== 'any' ? 1 : 0);

export const PRIORITY_META: Record<TaskPriority, { label: string; color: string; bg: string; rank: number }> = {
  URGENT: { label: 'Urgent', color: '#B42318', bg: '#FEE4E2', rank: 4 },
  HIGH: { label: 'High', color: '#B54708', bg: '#FEF0C7', rank: 3 },
  MEDIUM: { label: 'Medium', color: '#175CD3', bg: '#D1E9FF', rank: 2 },
  LOW: { label: 'Low', color: '#067647', bg: '#DCFAE6', rank: 1 },
};

export const SORT_LABEL: Record<TaskSort, string> = {
  manual: 'Manual order',
  due: 'Due date',
  priority: 'Priority',
  title: 'Title',
  created: 'Created',
  assignee: 'Assignee',
};

/** Due dates are days: a pick is stored as the end of that day, so a task is overdue only once its day is over. */
export const dueFromDay = (d: Dayjs | null) => (d ? d.endOf('day').toISOString() : null);

export function isOverdue(t: Pick<TaskItem, 'dueAt' | 'completedAt'>) {
  return !!t.dueAt && !t.completedAt && dayjs(t.dueAt).isBefore(dayjs().startOf('day'));
}

/** "Today", "Tomorrow", "Fri", "Mar 4", "Mar 4, 2027" */
export function dueLabel(iso: string) {
  const d = dayjs(iso);
  const today = dayjs().startOf('day');
  const diff = d.startOf('day').diff(today, 'day');
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff > 1 && diff < 7) return d.format('dddd');
  return d.year() === today.year() ? d.format('MMM D') : d.format('MMM D, YYYY');
}

const plain = (html: string | null) => (html ? html.replace(/<[^>]+>/g, ' ') : '');

function matchesSearch(t: TaskItem, q: string) {
  if (!q) return true;
  const needle = q.toLowerCase().replace(/^#/, '');
  return t.title.toLowerCase().includes(needle) || String(t.number) === needle || plain(t.description).toLowerCase().includes(needle);
}

function matchesFilters(t: TaskItem, f: TaskFilters) {
  if (f.assignees.length && !f.assignees.includes(t.assignee?.id ?? 'unassigned')) return false;
  if (f.priorities.length && !f.priorities.includes(t.priority ?? 'none')) return false;
  if (f.due !== 'any') {
    if (f.due === 'none') return !t.dueAt;
    if (!t.dueAt) return false;
    const d = dayjs(t.dueAt);
    const today = dayjs().startOf('day');
    if (f.due === 'overdue') return isOverdue(t);
    if (f.due === 'today') return d.isBefore(today.add(1, 'day')) && !d.isBefore(today);
    if (f.due === 'week') return d.isBefore(today.add(7, 'day'));
  }
  return true;
}

export function compareTasks(sort: TaskSort) {
  return (a: TaskItem, b: TaskItem) => {
    switch (sort) {
      case 'due':
        return (a.dueAt ? Date.parse(a.dueAt) : Infinity) - (b.dueAt ? Date.parse(b.dueAt) : Infinity) || a.position - b.position;
      case 'priority':
        return (b.priority ? PRIORITY_META[b.priority].rank : 0) - (a.priority ? PRIORITY_META[a.priority].rank : 0) || a.position - b.position;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'created':
        return Date.parse(b.createdAt) - Date.parse(a.createdAt);
      case 'assignee':
        return (a.assignee?.name ?? '￿').localeCompare(b.assignee?.name ?? '￿') || a.position - b.position;
      default:
        return a.position - b.position || Date.parse(a.createdAt) - Date.parse(b.createdAt);
    }
  };
}

export interface TaskTree {
  /** top-level rows that pass the filters, sorted */
  roots: TaskItem[];
  /** every loaded task's children, in manual order */
  children: Map<string, TaskItem[]>;
  /** parents to open because a subtask matched the search */
  autoExpand: Set<string>;
}

/**
 * Filters apply to top-level tasks; a search also keeps a task when one of its subtasks matches (and opens it).
 * `flat`: every task is a row of its own (My tasks, where subtasks assigned to you stand alone).
 */
export function buildTree(tasks: TaskItem[], filters: TaskFilters, sort: TaskSort, flat = false): TaskTree {
  const children = new Map<string, TaskItem[]>();
  if (!flat) {
    for (const t of tasks) if (t.parentId) children.set(t.parentId, [...(children.get(t.parentId) ?? []), t]);
    for (const list of children.values()) list.sort(compareTasks('manual'));
  }
  const autoExpand = new Set<string>();
  const searchHit = (t: TaskItem): boolean => {
    if (matchesSearch(t, filters.q)) return true;
    const hit = (children.get(t.id) ?? []).some(searchHit);
    if (hit) autoExpand.add(t.id);
    return hit;
  };
  const ids = new Set(tasks.map((t) => t.id));
  const roots = tasks
    // a subtask whose parent did not load (e.g. the parent is finished) still shows as a row
    .filter((t) => flat || !t.parentId || !ids.has(t.parentId))
    .filter((t) => matchesFilters(t, filters) && searchHit(t))
    .sort(compareTasks(sort));
  return { roots, children, autoExpand };
}

/** Every task that passes the filters, subtasks included (calendar). */
export function filterFlat(tasks: TaskItem[], filters: TaskFilters) {
  return tasks.filter((t) => matchesFilters(t, filters) && matchesSearch(t, filters.q));
}

export type DueBucket = 'overdue' | 'today' | 'tomorrow' | 'week' | 'later' | 'none';

export const DUE_BUCKETS: { key: DueBucket; title: string }[] = [
  { key: 'overdue', title: 'Overdue' },
  { key: 'today', title: 'Today' },
  { key: 'tomorrow', title: 'Tomorrow' },
  { key: 'week', title: 'Next 7 days' },
  { key: 'later', title: 'Later' },
  { key: 'none', title: 'No due date' },
];

export function dueBucket(t: Pick<TaskItem, 'dueAt' | 'completedAt'>): DueBucket {
  if (!t.dueAt) return 'none';
  const diff = dayjs(t.dueAt).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (diff < 0) return t.completedAt ? 'today' : 'overdue';
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff < 8) return 'week';
  return 'later';
}

/** The due date a task gets when dropped into a My tasks bucket (undefined: keep it). */
export function dueForBucket(bucket: DueBucket, current: string | null): string | null | undefined {
  const today = dayjs();
  if (bucket === 'none') return null;
  if (bucket === 'today') return dueFromDay(today);
  if (bucket === 'tomorrow') return dueFromDay(today.add(1, 'day'));
  // keep a date that already falls in the bucket
  if (current && dueBucket({ dueAt: current, completedAt: null }) === bucket) return undefined;
  if (bucket === 'week') return dueFromDay(today.add(2, 'day'));
  if (bucket === 'later') return dueFromDay(today.add(8, 'day'));
  return undefined;
}
